import {
  AuthData,
  AuthMethod,
  ProjectInfo,
} from '@infotech-soe/merps/types/session';

import IApiProvider from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import IIntlService from '../intl/IIntlService';
import IntlService from '../intl/IntlService';
import { IRuntimeConfigService } from '../rt-config/IRuntimeConfigService';
import RuntimeConfigService from '../rt-config/RuntimeConfigService';

import { IAuthService } from './IAuthService';
import { AvailableApplication, FeaturesList } from './types';

export interface SignInResult {
  userName?: string;
  Address?: string | null;
  Token?: string | null;
}

/**
 * Default implementation of {@link IAuthService}
 * that uses {@link IApiProvider}'s `signInWithLogin` and `signOut` methods.
 */
class AuthServiceSingleton implements IAuthService {
  private readonly apiProvider: IApiProvider;
  private readonly rtConfigService: IRuntimeConfigService;
  private readonly intlService: IIntlService;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
    this.rtConfigService = new RuntimeConfigService();
    this.intlService = new IntlService();
  }

  public async signIn(
    data: AuthData,
    application: string | null
  ): Promise<SignInResult> {
    if (data.type === AuthMethod.LOGIN) {
      const { newPassword, password, userName } = data;

      return this.apiProvider.auth.signIn(
        userName,
        password,
        application,
        newPassword || null
      );
    }

    if (data.type === AuthMethod.DIGITAL_SIGNATURE) {
      const { signature } = data;
      return this.apiProvider.auth.signIn(
        null,
        null,
        application,
        null,
        signature
      );
    }

    if (data.type === AuthMethod.WINDOWS_AD) {
      return this.apiProvider.auth.signInWithWindowsAD(
        application,
        data.userName,
        data.password
      );
    }

    if (data.type === AuthMethod.KERBEROS) {
      return this.apiProvider.auth.loginWithKerberos(application);
    }

    throw new Error('unknown type of authN');
  }

  public signOut(): Promise<void> {
    return this.apiProvider.auth.signOut();
  }

  public getSchemaCrc(): Promise<string> {
    return this.apiProvider.auth.getSchemaCrc();
  }

  public async getFeaturesList(): Promise<FeaturesList> {
    const {
      apps: appNames,
      userApps: userAppNames,
      authMethods,
      timezone,
    } = await this.apiProvider.auth.getFeaturesList();
    const config = await this.rtConfigService.getRuntimeApiConfig();
    const excludedApps = config.excludedApps ?? [];
    const excludedAuthMethods = config.excludedAuthMethods ?? [];
    const excludedEDSAgents = config.excludedEDSAgents ?? [];
    const dsKeyType = config.dsKeyType ?? undefined;
    const allowedDSMediaTypes = config.allowedDSMediaTypes ?? undefined;
    const forbiddenDSMediaTypes = config.forbiddenDSMediaTypes ?? undefined;
    const apps: AvailableApplication[] = [];
    const signAlgo = config.signAlgo ?? undefined;

    for (const name of appNames) {
      if (!excludedApps.includes(name)) {
        apps.push({
          name,
          displayName: await this.intlService.getApplicationName(name),
        });
      }
    }

    (Object.keys(authMethods) as AuthMethod[]).forEach(key => {
      if (excludedAuthMethods.includes(key)) {
        authMethods[key] = false;
      }
    });

    const userApps: AvailableApplication[] = [];
    for (const name of userAppNames ?? []) {
      if (!excludedApps.includes(name)) {
        userApps.push({
          name,
          displayName: await this.intlService.getApplicationName(name),
        });
      }
    }

    return {
      apps,
      userApps,
      authMethods,
      excludedEDSAgents,
      dsKeyType,
      allowedDSMediaTypes,
      forbiddenDSMediaTypes,
      timezone,
      signAlgo,
    };
  }

  public async getProjectInfo(): Promise<ProjectInfo> {
    const prInfo = await this.apiProvider.auth.getProjectInfo();
    if (prInfo.name) {
      prInfo.name = await this.intlService.getApplicationName(prInfo.name);
    }
    return prInfo;
  }
}

const AuthService = (() => {
  let instance: IAuthService;
  return new Proxy(AuthServiceSingleton, {
    construct(Target: typeof AuthServiceSingleton): IAuthService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default AuthService;
